/**
 * Plugin support matrix pane (Task 18).
 *
 * Renders the response of `GET /api/plugins` as a table that the user
 * can open from the settings / info area of the Obsidian-styled shell.
 *
 * Acceptance for Task 18:
 *   "UI shows table: plugin name, status badge, notes."
 *
 * Design:
 *   - Pure presentational component. Data is fetched by the parent (or
 *     by the small `usePluginMatrix` hook below for parents that prefer
 *     a one-liner). This mirrors `OutlinePane` / `BacklinksPane`.
 *   - Status badges have stable test-friendly class names
 *     (`ow-plugin-badge ow-plugin-badge--<status>`) so themes can colour
 *     them (`--callout-success`, `--callout-warning`, `--callout-error`)
 *     and snapshot tests can assert which badge rendered.
 *   - Plugin names link to the upstream homepage when known; otherwise
 *     render as plain text. The endpoint always emits `homepage: string | null`.
 *   - Diagnostics (e.g. "vault has no .obsidian/") are rendered above
 *     the table in a callout-styled banner so users understand why their
 *     list might be empty.
 *
 * The pane is rendered into the Shell's right sidebar tab area. The
 * Shell already advertises a "Plugin matrix" activity icon (see
 * `Shell.tsx` ActivityBar) — wiring that icon to *show* this pane is a
 * Shell concern; this component is the pane itself.
 */

import { useEffect, useState } from 'react';

/* ── Wire shape (mirrors the server route) ──────────────────────────── */

export type PluginMatrixStatus = 'native' | 'display-only' | 'unsupported' | 'unknown';

export interface PluginMatrixRow {
  readonly id: string;
  readonly name: string;
  readonly status: PluginMatrixStatus;
  readonly renderer: string | null;
  readonly notes: string;
  readonly homepage: string | null;
  readonly installed: boolean;
}

export interface PluginMatrixResponse {
  readonly rows: readonly PluginMatrixRow[];
  readonly diagnostics: ReadonlyArray<{ readonly code: string; readonly message: string }>;
  readonly installedCount: number;
  readonly recognisedCount: number;
}

/* ── Status labels and ordering ─────────────────────────────────────── */

const STATUS_LABEL: Readonly<Record<PluginMatrixStatus, string>> = {
  native: 'Native',
  'display-only': 'Display only',
  unsupported: 'Unsupported',
  unknown: 'Unknown',
};

const STATUS_DESCRIPTION: Readonly<Record<PluginMatrixStatus, string>> = {
  native:
    'Rendered with built-in ObsidianWeb code — the plugin does not need to run.',
  'display-only':
    'A read-only projection of the plugin’s output is rendered on the web. Editing flows go through the desktop client.',
  unsupported:
    'Recognised by ObsidianWeb but cannot be rendered on the web (depends on the Obsidian plugin runtime).',
  unknown:
    'Not in ObsidianWeb’s support matrix. Output that depends on this plugin may render as plain markdown.',
};

/* ── Props ──────────────────────────────────────────────────────────── */

export interface PluginMatrixPaneProps {
  /** Response payload, or `null` while loading. */
  readonly data: PluginMatrixResponse | null;
  /** Loading flag. */
  readonly loading?: boolean;
  /** Error string; rendered above the table when set. */
  readonly errorMessage?: string | null;
  /**
   * Optional click handler — called when the user clicks the plugin row.
   * Useful for the future "show me what this plugin produces" preview.
   * The current Task 18 acceptance only requires the table itself.
   */
  readonly onSelect?: (row: PluginMatrixRow) => void;
}

/* ── Small reusable bits ────────────────────────────────────────────── */

function StatusBadge({ status }: { status: PluginMatrixStatus }): JSX.Element {
  return (
    <span
      className={`ow-plugin-badge ow-plugin-badge--${status}`}
      data-testid="plugin-status-badge"
      data-status={status}
      title={STATUS_DESCRIPTION[status]}
    >
      {STATUS_LABEL[status]}
    </span>
  );
}

function PluginName({ row }: { row: PluginMatrixRow }): JSX.Element {
  if (row.homepage === null) {
    return <span className="ow-plugin-name">{row.name}</span>;
  }
  return (
    <a
      className="ow-plugin-name ow-plugin-name--link"
      href={row.homepage}
      target="_blank"
      rel="noreferrer noopener"
    >
      {row.name}
    </a>
  );
}

/* ── Pane ───────────────────────────────────────────────────────────── */

export function PluginMatrixPane(props: PluginMatrixPaneProps): JSX.Element {
  if (props.loading === true && props.data === null) {
    return (
      <aside
        className="ow-plugin-matrix-pane"
        aria-label="Plugin support matrix"
        data-testid="plugin-matrix-pane"
      >
        <div className="ow-plugin-matrix-header">Plugin support</div>
        <p className="ow-plugin-matrix-empty">Loading…</p>
      </aside>
    );
  }

  if (props.errorMessage) {
    return (
      <aside
        className="ow-plugin-matrix-pane"
        aria-label="Plugin support matrix"
        data-testid="plugin-matrix-pane"
      >
        <div className="ow-plugin-matrix-header">Plugin support</div>
        <p
          className="ow-plugin-matrix-error"
          role="alert"
          data-testid="plugin-matrix-error"
        >
          {props.errorMessage}
        </p>
      </aside>
    );
  }

  const data = props.data;
  if (data === null) {
    // No data yet, no error, not flagged loading — render empty shell.
    return (
      <aside
        className="ow-plugin-matrix-pane"
        aria-label="Plugin support matrix"
        data-testid="plugin-matrix-pane"
      >
        <div className="ow-plugin-matrix-header">Plugin support</div>
        <p className="ow-plugin-matrix-empty">No data.</p>
      </aside>
    );
  }

  return (
    <aside
      className="ow-plugin-matrix-pane"
      aria-label="Plugin support matrix"
      data-testid="plugin-matrix-pane"
    >
      <div className="ow-plugin-matrix-header">
        <span>Plugin support</span>
        <span
          className="ow-plugin-matrix-counts"
          data-testid="plugin-matrix-counts"
        >
          {data.recognisedCount} of {data.installedCount} installed plugins recognised
        </span>
      </div>

      {data.diagnostics.length > 0 ? (
        <ul
          className="ow-plugin-matrix-diagnostics"
          data-testid="plugin-matrix-diagnostics"
          role="list"
        >
          {data.diagnostics.map((d, i) => (
            <li key={`${d.code}:${i}`} data-code={d.code}>
              <span className="ow-plugin-diagnostic-code">{d.code}</span>
              <span className="ow-plugin-diagnostic-message">{d.message}</span>
            </li>
          ))}
        </ul>
      ) : null}

      {data.rows.length === 0 ? (
        <p className="ow-plugin-matrix-empty" data-testid="plugin-matrix-empty">
          No community plugins enabled in this vault.
        </p>
      ) : (
        <table className="ow-plugin-matrix-table" data-testid="plugin-matrix-table">
          <thead>
            <tr>
              <th scope="col">Plugin</th>
              <th scope="col">Status</th>
              <th scope="col">Notes</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((row) => (
              <tr
                key={row.id}
                className={`ow-plugin-row ow-plugin-row--${row.status} ${
                  row.installed ? 'ow-plugin-row--installed' : 'ow-plugin-row--catalogue'
                }`}
                data-testid="plugin-matrix-row"
                data-plugin-id={row.id}
                data-status={row.status}
                data-installed={row.installed ? '1' : '0'}
                onClick={
                  props.onSelect
                    ? (): void => {
                        props.onSelect?.(row);
                      }
                    : undefined
                }
              >
                <td className="ow-plugin-cell ow-plugin-cell--name">
                  <PluginName row={row} />
                  <code className="ow-plugin-id">{row.id}</code>
                </td>
                <td className="ow-plugin-cell ow-plugin-cell--status">
                  <StatusBadge status={row.status} />
                  {!row.installed ? (
                    <span
                      className="ow-plugin-catalogue-hint"
                      title="Not enabled in this vault"
                    >
                      (catalogue)
                    </span>
                  ) : null}
                </td>
                <td className="ow-plugin-cell ow-plugin-cell--notes">{row.notes}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </aside>
  );
}

/* ── Optional fetching hook ─────────────────────────────────────────── */

export interface UsePluginMatrixState {
  readonly data: PluginMatrixResponse | null;
  readonly loading: boolean;
  readonly error: string | null;
}

/**
 * Tiny fetch hook backing the Shell's plugins tab (Shell.tsx renders
 * `<PluginMatrixPane>` through its thin PluginsPane wrapper). Kept in this
 * module so the pane is self-contained.
 *
 * Options:
 *   - `includeCatalogue: true` → fetches `/api/plugins?catalogue=1` to
 *     also see uninstalled-but-known plugins.
 */
export function usePluginMatrix(options: {
  readonly includeCatalogue?: boolean;
  /** Override the fetcher (used by tests). */
  readonly fetcher?: (url: string) => Promise<unknown>;
} = {}): UsePluginMatrixState {
  const [state, setState] = useState<UsePluginMatrixState>({
    data: null,
    loading: true,
    error: null,
  });

  useEffect(() => {
    let cancelled = false;
    const url =
      options.includeCatalogue === true ? '/api/plugins?catalogue=1' : '/api/plugins';

    const run = async (): Promise<void> => {
      try {
        const fetcher = options.fetcher ?? defaultFetcher;
        const raw = await fetcher(url);
        if (cancelled) return;
        // Light client-side shape check — the server already validates.
        if (!isResponseShape(raw)) {
          setState({
            data: null,
            loading: false,
            error: 'malformed /api/plugins response',
          });
          return;
        }
        setState({ data: raw, loading: false, error: null });
      } catch (err) {
        if (cancelled) return;
        setState({
          data: null,
          loading: false,
          error: err instanceof Error ? err.message : String(err),
        });
      }
    };

    void run();
    return (): void => {
      cancelled = true;
    };
  }, [options.includeCatalogue, options.fetcher]);

  return state;
}

async function defaultFetcher(url: string): Promise<unknown> {
  const res = await fetch(url, { headers: { accept: 'application/json' } });
  if (!res.ok) {
    throw new Error(`GET ${url} → ${res.status}`);
  }
  return (await res.json()) as unknown;
}

function isResponseShape(v: unknown): v is PluginMatrixResponse {
  if (typeof v !== 'object' || v === null) return false;
  const o = v as Record<string, unknown>;
  return (
    Array.isArray(o['rows']) &&
    Array.isArray(o['diagnostics']) &&
    typeof o['installedCount'] === 'number' &&
    typeof o['recognisedCount'] === 'number'
  );
}

export default PluginMatrixPane;
